import { Brand, DeckPrint, DeckViewer, Frame, Img } from "../headliner/DeckShell";
import { CinematicCover, CINE_ASSETS } from "../headliner/CinematicCover";
import { Room700 } from "../headliner/HeadlinerDeck";
import { TEAM } from "../headliner/TeamAndPartners";
import { KOMITE, LEVERES, LEVERES_ASSETS } from "../investordeck/InvestorSlides";
import { recipientOf } from "../investordeck/recipients";
import { Nettverket, NETT_ASSETS } from "../teamdeck/TeamDeck";
import "../investordeck/investor.css";
import "../visiondeck/vision-deck.css";
import "./konsept.css";

/* Konseptdeck (norsk) — forklarer visjonen og konseptet uten å be om penger. For programkomité-kandidater, norske talere, ambassadører,
   vertskap og alle som skal kunne videresende «se på dette» internt. Regel: ingen tall om økonomi, ingen forespørsel. Passordbeskyttet (Kevin navngis;
   kunngjøringsgaten G1b gjelder), personlig lenke via LinkBuilder. 16 slides (14 med rolle-flagg). Språk: stramt norsk, «kunstig intelligens» første gang på en slide, «AI» deretter;
   formatnavn beholdes (Build Lab, AI Experience, Summit Dinner, The Next Shift).
   Variant (kategori): «nasjonal» (standard — byen er scenen, ikke argumentet) · «bergen» (slide 02 «Byen»: Bergen er argumentet; Bergen-idé på «Måter å bli med»).
   Data deles med investordecket (LEVERES, KOMITE, mottaker-hooks), ensideren (Nettverket) og Vision-decket (fakta om Norge, stemmene, konseptbildene). */

const F = (props) => <Frame prep="Utarbeidet for" {...props} />;
const KEVIN = "/images/headliner/kevin-src-hi.webp";
const IMG = {
  scene: "/images/build-stage.webp", publikum: "/images/peer-gynt-publikum.webp", hus: "/images/grieghallen.webp",
  bergen: "/images/bergen-night.webp", rom: "/images/what-room-v4.webp", expo: "/images/vision-expo-v3.webp", nett: "/images/vision-networking-v3.webp",
  dinner: "/images/vision-dinner-v4.webp", broadcast: "/images/vision-broadcast.webp",
};
const stOn = (st) => /^(Ja|Tilbud|Interesse)/.test(st);
const ordOf = (c) => (c || "").toLowerCase().split(/\s+/);
const bergenOf = (c) => ordOf(c).includes("bergen");
/* Rolle-flagg fra LinkBuilder («-programkomite» osv.) gjør 15/16 personlige og kutter det som ikke angår rollen */
const ROLLE_ID = ["programkomite", "taler", "ambassador", "partner"];
const rolleOf = (c) => ROLLE_ID.find((r) => ordOf(c).includes(`-${r}`)) || "";

const Ph = ({ src, alt, cap, className = "", testid }) => (
  <figure className={`kd-ph ${className}`} data-testid={testid}>
    <span className="im"><Img src={src} alt={alt} draggable={false} /></span>
    <figcaption>{cap}</figcaption>
  </figure>
);

/* 01 Forside — «· For X», ikke «×» (× leses som samarbeid). «Partnere og dialoger», ikke «Leveres med» (status oppgis på 04). */
const Cover = ({ mottaker }) => (
  <CinematicCover
    className="ms-cover iv-cover kd-cover"
    testid="kd-cover"
    kevinCap={["Foreslått åpningstaler", "Kevin O'Leary", "Tilgjengelig 13. april · ikke kontrahert"]}
    lockup={mottaker
      ? <p className="bd-lockup kd-lockup" data-testid="kd-lockup"><Brand size={19} /><span className="for">· For {mottaker}</span></p>
      : <p className="bd-lockup" data-testid="kd-lockup"><Brand size={19} /></p>}
    label={<><b>Konseptet</b>{mottaker ? `Utarbeidet for ${mottaker} · ` : ""}Fortrolig</>}
    title={["Norwegian", "AI Summit"]}
    sub={<><span className="l1">700 ledere. Én dag. Grieghallen, Bergen.</span>En nasjonal konferanse om kunstig intelligens. Tema 2027: <b style={{ whiteSpace: "nowrap" }}>The Next Shift</b>.</>}
    index={[
      ["Dato · Sted", "13. april 2027 · Grieghallen, Bergen"],
      ["Dokumentet", "Konseptet · hva, hvorfor og hvem", "", "kd-cover-position"],
      ["Partnere og dialoger", "Unite Event · Cure · Grieghallen · Replit", "ms-cover-coal", "kd-cover-coalition"],
    ]}
  />
);

/* 02 Arenaen (nasjonal, standard) — «mangler én samlende arena», ikke «har ingen konferanse» (lett å utfordre). Salen som bilde. Mottaker-hook nederst. */
const ARENA = [
  ["Behovet", "Det største teknologiskiftet i norsk næringsliv på en generasjon — uten ett sted der ledelse, kapital, teknologi og politikk møtes samme dag."],
  ["Formen", "700 inviterte ledere, én sal, ett program. Alle hører de samme argumentene og går ut med de samme referansene."],
  ["Stedet", "Grieghallen i Bergen. Byen er scenen, ikke argumentet — publikummet kommer fra hele landet."],
  ["Ambisjonen", "En fast dag i året der norsk næringsliv samles for å diskutere og sette retning for hvordan landet skal bruke kunstig intelligens."],
];
const Arenaen = ({ nr, mottaker }) => (
  <F nr={nr} label="Arenaen" mottaker={mottaker} className="hd-bergen iv-by kd-by hd-light">
    <Img className="hd-bergen-img" src={IMG.rom} alt="Salen" draggable={false} />
    <h2 className="hd-h2" data-testid="kd-arena-h"><span>Norge mangler én samlende</span><span>nasjonal AI-arena.</span><span>Vi bygger den.</span></h2>
    <p className="hd-bergen-sub" data-testid="kd-arena-sub">Norsk næringsliv har sine faste møteplasser — for energi, for finans, for eierskap. Kunstig intelligens har mange arrangementer, men ingen samlende arena der ledelse, kapital, teknologi og politikk møtes samme dag. 13. april 2027 samler vi 700 ledere fra hele landet til én dag i Grieghallen: ett program, internasjonale hovedtalere, ingen parallelle sesjoner.</p>
    <ul className="hd-ledger" data-testid="kd-arena-points">{ARENA.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}</ul>
    <p className="hd-bergen-foot" data-testid="kd-arena-foot">{recipientOf(mottaker)?.hook || "Internasjonalt nivå, nasjonalt publikum, én dato i året."}</p>
  </F>
);

/* 02 Byen (variant bergen) — Bergen er argumentet: byen, kompetansen, miljøet, ambisjonen. Ingen kapital-rad (hører til investordecket). */
const BY = [
  ["Byen", "Norges nest største næringsregion: hav, energi, finans, medier og teknologi — næringer der kunstig intelligens allerede er i drift."],
  ["Kompetansen", "UiB, NHH, HVL og Media City Bergen. Forskerne og studentene får plass på programmet, ikke bare i salen."],
  ["Miljøet", "Klyngene, VIS og gründerne rundt dem. AI Experience i foajeen viser det som bygges her."],
  ["Ambisjonen", "En fast dag i året der norsk næringsliv samles for å sette retning — forankret i Bergen, med publikum fra hele landet."],
];
const ByenBergen = ({ nr, mottaker }) => (
  <F nr={nr} label="Byen" mottaker={mottaker} className="hd-bergen iv-by kd-by hd-light">
    <Img className="hd-bergen-img" src={IMG.bergen} alt="Bergen om natten, sett fra Fløyen" draggable={false} />
    <span className="hd-credit">Foto · Svein-Magne Tunli · CC BY-SA 4.0</span>
    <h2 className="hd-h2" data-testid="kd-by-h"><span>Norge mangler én samlende</span><span>nasjonal AI-arena.</span><span>Vi bygger den i Bergen.</span></h2>
    <p className="hd-bergen-sub" data-testid="kd-by-sub">Norsk næringsliv har sine faste møteplasser, de fleste i Oslo. Kunstig intelligens har mange arrangementer, men ingen samlende arena. 13. april 2027 samler vi 700 ledere fra hele landet i Grieghallen — med Bergen som vertskap, og med byens fagmiljøer, klynger og gründere på programmet.</p>
    <ul className="hd-ledger" data-testid="kd-by-points">{BY.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}</ul>
    <p className="hd-bergen-foot" data-testid="kd-by-foot">{recipientOf(mottaker)?.hook || "Internasjonalt nivå, nasjonalt publikum, Bergen som vertskap."}</p>
  </F>
);

/* 03 Momentum — sosialt bevis rett etter konseptet: hovedtaleren og Build Partner, side om side, status på sliden. Overskriften lover ikke mer enn fotnoten.
   Erstatter også den dedikerte Kevin-sliden (fortrolighetslinjen ligger her). Tall: Replits egne (11. mars 2026). */
const MED = [
  {
    id: "kevin", k: "Hovedtaleren", n: "Kevin O'Leary", r: "Investor og gründer · kjent fra Shark Tank · foreslått åpningstaler",
    st: "Tilgjengelighet bekreftet for 13. april 2027 · ikke kontrahert",
    d: "Åpner dagen på hovedscenen. Direkte, kommersielt, uten pynt — det registeret resten av dagen bygges i. Valgt fordi han snakker om kunstig intelligens som eier, ikke som teknolog.",
    facts: [["09.30", "åpner hovedscenen"], ["2,5 t", "i huset: foredrag, samtale, lukket møte"], ["15–20", "ledere i det lukkede møtet"]],
    visual: <Img src={KEVIN} alt="Kevin O'Leary" draggable={false} />,
  },
  {
    id: "replit", k: "Build Partner", n: "Replit", r: "Official Build Partner · Build Lab og byggingen på hovedscenen",
    st: "Ja i prinsippet · avtale formaliseres oktober 2026",
    d: "Lar hvem som helst bygge programvare ved å beskrive den. På Norwegian AI Summit tar Replit et reelt forretningsproblem og bygger en fungerende løsning gjennom dagen — foran 700 ledere.",
    facts: [["9 mrd. USD", "verdsettelse etter Serie D, mars 2026"], ["50+ mill.", "brukere"], ["85 %", "av Fortune 500 har ansatte som bygger på Replit"]],
    visual: <i className="mask replit" style={{ "--m": "url(/logos/partners/replit.svg)" }} aria-label="Replit" />,
  },
];
const Momentum = ({ nr, mottaker }) => (
  <F nr={nr} label="Momentum" mottaker={mottaker} className="kd-med hd-light">
    <h2 className="hd-h2" data-testid="kd-med-h">Momentum allerede nå.</h2>
    <p className="kd-lede" data-testid="kd-med-lede">To navn viser ambisjonsnivået før programmet er satt: hovedtaleren som åpner dagen, og selskapet som bygger på scenen. Status står ved begge.</p>
    <div className="kd-med-cols">
      {MED.map((t) => (
        <section className={`kd-med-col kd-med-${t.id}`} key={t.id} data-testid={`kd-med-${t.id}`}>
          <span className="k">{t.k}</span>
          <div className="row">
            <div className="ph">{t.visual}</div>
            <div className="txt">
              <span className="n">{t.n}</span>
              <span className="r">{t.r}</span>
              <span className="st on">{t.st}</span>
              <p className="d">{t.d}</p>
            </div>
          </div>
          <dl className="facts">{t.facts.map(([v, l]) => <div key={l}><dt>{v}</dt><dd>{l}</dd></div>)}</dl>
        </section>
      ))}
    </div>
    <p className="bd-foot kd-legend" data-testid="kd-med-foot"><b>Fortrolig:</b> Kevin O'Learys navn offentliggjøres ikke før kontrakten er signert. Ingenting oppgis som avtalt før det er det. Tall om Replit: Replits egne, 11. mars 2026.</p>
  </F>
);

/* 04 Hvem står bak — fire portretter som hovedmotiv, leveransepartnere og forankring som ett bånd under. Status ved hvert navn. */
const ROLLE = {
  "Martin Kviteberg": ["Daglig leder", "Program, partnere og gjennomføring"],
  "Sarah Sleeman": ["Partner", "Drift og opplevelse"],
  "Sigrid Kviteberg": ["Partner", "Teknologi · Global InTech AS"],
  "Vernand Morency": ["Internasjonale relasjoner og partnerskap", "Tidligere NFL — Green Bay Packers, Houston Texans. Eiendomsutvikler, Washington DC."],
};
const KORT = { "VIS — Vestlandets innovasjonsselskap": ["VIS", "Vestlandets innovasjonsselskap"] };
const KOM_ID = { "Bergen Næringsråd": "naeringsrad", "Handelshøyskolen BI": "bi", "Medieklyngen": "medieklyngen", "VIS — Vestlandets innovasjonsselskap": "vis" };
const HvemStarBak = ({ nr, mottaker }) => (
  <F nr={nr} label="Hvem står bak" mottaker={mottaker} className="kd-who hd-light">
    <h2 className="hd-h2" data-testid="kd-who-h">Hvem står bak.</h2>
    <p className="kd-lede" data-testid="kd-who-sub">Norwegian AI Summit er startet og eies av et lite team med internasjonal erfaring. Konferansen leveres sammen med etablerte partnere innen produksjon, kommunikasjon og arena, og forankres i næringslivets organisasjoner og akademia. Status står ved hvert navn.</p>
    <div className="kd-band kd-team" data-testid="kd-who-team">
      <span className="k">Teamet</span>
      <ul>{TEAM.map(([n, , img]) => <li key={n} data-testid={`kd-team-${n.split(" ")[0].toLowerCase()}`}><Img src={img} alt={n} className="ph" draggable={false} /><b>{n}</b><i>{ROLLE[n][0]}</i><em>{ROLLE[n][1]}</em></li>)}</ul>
    </div>
    <div className="kd-band kd-lev" data-testid="kd-who-lev">
      <span className="k">Leveres med</span>
      <ul>{LEVERES.map(([n, logo, cls, r, st]) => <li key={n} data-testid={`kd-lev-${cls}`}><i className={`mask ${cls}`} style={{ "--m": `url(${logo})` }} aria-label={n} /><span className="r">{r}</span><span className={`st ${stOn(st) ? "on" : ""}`}>{st}</span></li>)}</ul>
    </div>
    <div className="kd-band kd-kom" data-testid="kd-who-kom">
      <span className="k">Forankring · programkomité under etablering</span>
      <ul>{KOMITE.map(([n, r, st]) => { const [navn, em] = KORT[n] || [n, r]; return <li key={n} data-testid={`kd-kom-${KOM_ID[n]}`}><b>{navn}</b><em>{em}</em><span className={`st ${stOn(st) ? "on" : ""}`}>{st}</span></li>; })}</ul>
    </div>
    <p className="bd-foot kd-legend" data-testid="kd-who-legend"><b>Ja i prinsippet</b> — sagt ja, avtale formaliseres · <b>Tilbud mottatt</b> — skriftlig, ikke signert · <b>Interesse bekreftet</b> — sagt ja til å gå videre · <b>Vist interesse</b> — positiv første kontakt · <b>Foreslått / I dialog</b> — ingenting avtalt</p>
  </F>
);

/* 05 Nettverket rundt — fra ensideren (logoer, status per organisasjon) */
const NettverketNo = ({ nr, mottaker }) => <Nettverket nr={nr} mottaker={mottaker} prep="Utarbeidet for" label="Nettverket rundt" />;

/* 06 Rommet — Vision-deckets «700»: tallet som hovedmotiv, ett punkt per sete (28×25), hvem som sitter i salen. */
const ROM = [
  ["Ledelse", "Konsernsjefer, styremedlemmer og eiere — de som skal ta beslutningene."],
  ["Kapital", "Investorer, banker og familiekontorer — de som skal finansiere dem."],
  ["Teknologi", "Gründere, teknologiledere og forskere — de som bygger løsningene."],
  ["Politikk", "Nasjonale og regionale beslutningstakere — de som setter rammene."],
];
const Rommet = ({ nr, mottaker }) => (
  <F nr={nr} label="Rommet" mottaker={mottaker} className="hd-room kd-room hd-light">
    <div className="hd-big" data-testid="kd-rom-h">700</div>
    <p className="hd-sub" data-testid="kd-rom-sub">Seter. Kuratert for dem som bestemmer hvordan<br />kunstig intelligens bygges, kjøpes og styres i Norge.</p>
    <p className="hd-room-line" data-testid="kd-rom-line">Etter invitasjon · Betalte seter · Én sal, ett program · Grieghallen, Bergen · 13. april 2027</p>
    <Room700 />
    <p className="hd-room700-cap hd-room-cap" data-testid="kd-rom-cap">Ett punkt per sete. <b>700</b> — ikke 7 000. Rommet er produktet.</p>
    <div className="hd-side" data-testid="kd-rom-points">
      <span className="k">Hvem sitter i salen</span>
      <ul>{ROM.map(([t, d]) => <li key={t}><b>{t}</b><span>{d}</span></li>)}</ul>
    </div>
  </F>
);

/* 07 Temaet — The Next Shift i tre spalter, og skiftet som tidslinje nederst (fra IT-avdelingen til styrerommet) */
const TEMA = [
  ["Fra verktøy til drift", "Kunstig intelligens går fra noe ansatte prøver til noe virksomheten kjører på: kundeservice, planlegging, produksjon, saksbehandling.", "Det er her verdien — og risikoen — faktisk oppstår."],
  ["Fra pilot til ansvar", "Når systemene tar beslutninger, flytter spørsmålene seg til styrerommet: ansvar, sikkerhet, konkurransekraft og arbeidsplasser.", "Programmet er laget for dem som må svare."],
  ["Fra å snakke om det til å vise det", "Det som presenteres på scenen, finnes. Løsninger bygges gjennom dagen, og eksemplene kommer fra virksomheter som har tatt dem i bruk.", "Ingen framtidsvisjoner uten dekning."],
];
const SKIFT = [
  ["2019–2023", "IT-avdelingen", "Piloter, konsepttester, noen få entusiaster."],
  ["2024–2026", "Budsjettlinjen", "Reelle kostnader, reell risiko — og den første reguleringen, når EUs AI-forordning kommer til Norge gjennom EØS."],
  ["2027", "Styrerommet", "Strategi, arbeidsstyrke, kapital. Året beslutningene tas — og året salen møtes."],
];
const Temaet = ({ nr, mottaker }) => (
  <F nr={nr} label="Temaet" mottaker={mottaker} className="kd-tema hd-light">
    <h2 className="hd-h2" data-testid="kd-tema-h">The Next Shift.</h2>
    <p className="kd-lede" data-testid="kd-tema-lede">Det første skiftet gjorde kunstig intelligens tilgjengelig for alle. Det neste flytter den inn i driften, beslutningene og organisasjonen. Det er skiftet 2027 handler om — og derfor er programmet laget for ledere, ikke for spesialister.</p>
    <div className="kd-cols" data-testid="kd-tema-cols">
      {TEMA.map(([k, v, n], i) => <section className="kd-col" key={k}><span className="k"><i>0{i + 1}</i>{k}</span><p className="v">{v}</p><p className="n">{n}</p></section>)}
    </div>
    <ol className="hd-shift kd-shift" data-testid="kd-tema-shift">
      {SKIFT.map(([y, t, d], i) => <li key={y} className={i === SKIFT.length - 1 ? "now" : ""}><span className="y">{y}</span><i className="pt" /><span className="t">{t}</span><span className="d">{d}</span></li>)}
    </ol>
  </F>
);

/* 08 Hvorfor Norge — verifiserte fakta (Stargate Norway/Narvik, Microsoft 2019, Google Skien 2024). Samme kilder som Vision-decket. */
const NORGE = [
  ["Regnekraft", "Stargate Norway — OpenAIs første europeiske anlegg under «OpenAI for Countries» — bygges utenfor Narvik av Nscale og Aker: 230 MW i første fase, 100 000 NVIDIA-GPU-er innen utgangen av 2026."],
  ["Sky", "Microsoft har driftet to norske skyregioner siden 2019. Google satte spaden i jorden for et datasenter til 600 millioner euro i Skien i 2024."],
  ["Kraft", "Vannkraft, kjølig klima og en strømmiks som nesten bare er fornybar — grunnen til at infrastrukturen kommer hit."],
  ["Markedet", "Et land med høy tillit og høy teknologibruk: et oljefond, en stor offentlig sektor, energi, finans, hav og industri — næringene som sitter i salen."],
];
const HvorforNorge = ({ nr, mottaker }) => (
  <F nr={nr} label="Hvorfor Norge" mottaker={mottaker} className="hd-cant vd-norway kd-norge hd-light">
    <h2 className="hd-h2" data-testid="kd-norge-h">Infrastrukturen kommer til Norge.<br />Beslutningene må tas her.</h2>
    <ul className="hd-ledger" data-testid="kd-norge-points">{NORGE.map(([k, v]) => <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>)}</ul>
    <p className="hd-cant-foot" data-testid="kd-norge-foot">Infrastrukturen kommer uansett. Hva som skal kjøre på den, bestemmes av folk som sitter i denne salen.</p>
  </F>
);

/* 09 Programmet — fem stemmer, én scene. Kurateringsfilosofien, ikke navnelisten (målnavn hører ikke hjemme i et deck som videresendes). */
const STEMMER = [
  ["Ledelse", "Konsernsjefer og styremedlemmer om hva kunstig intelligens endrer i hvordan selskaper ledes, finansieres og drives.", "Hva gjør vi selv, hva kjøper vi — og hva lar vi være?"],
  ["Forskning", "Forskerne som vet hva modellene kan og ikke kan — og hva som kommer.", "Hvor går grensen for hva modellene faktisk kan i 2027?"],
  ["Frontlinjen", "Laboratoriene og plattformene som bygger teknologien — på hovedscenen, ikke på en stand.", "Hva bygges nå som endrer norske virksomheter om to år?"],
  ["Kapital", "Investorer og eiere om hvor pengene går, og hva de nå forventer av selskapene de eier.", "Hva må et selskap vise for å bli finansiert i 2027?"],
  ["Politikk", "Regulatorer og politikere om reglene som kommer til å forme tiåret.", "Hvilke regler kommer, og hva betyr de for et norsk styre?"],
];
const Programmet = ({ nr, mottaker }) => (
  <F nr={nr} label="Programmet" mottaker={mottaker} className="vd-prog vd-voices kd-prog hd-light">
    <h2 className="hd-h2" data-testid="kd-prog-h">Fem stemmer. Én scene.<br />Et program under kuratering.</h2>
    <p className="vd-prog-sub" data-testid="kd-prog-sub">Ledelse, forskning, teknologiens frontlinje, kapital og politikk — på én scene, uten parallelle spor. Hver sesjon forberedes og ledes av konferansen. Navn offentliggjøres når de er signert; det endelige programmet bekreftes fortløpende.</p>
    <ol className="vd-voices-grid" data-testid="kd-prog-grid">{STEMMER.map(([t, d, q], i) => <li key={t}><span className="n">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span><span className="q"><i>Spørsmålet fra scenen</i>{q}</span></li>)}</ol>
    <p className="vd-voices-foot" data-testid="kd-prog-foot"><b>Tilgjengelighet bekreftet så langt</b> — Kevin O'Leary, foreslått åpningstaler · Silvija Seres. Datoene er bekreftet ledige; ingen kontrakt er signert. Resten av programmet kurateres nå, sammen med programkomiteen.</p>
  </F>
);

/* 10 Dagen — tidslinje med innhold per blokk, publikum som stripe under */
const DAG = [
  ["09:00", "Ankomst", "Registrering. AI Experience i foajeen åpner."],
  ["09:30", "Åpningsforedraget", "Kevin O'Leary (foreslått). Setter dagens register.", true],
  ["11:30", "Ledersamtalene", "Lukkede samtaler i mindre grupper. Ingen referat."],
  ["13:00", "AI Experience · Build Lab", "Lunsj i foajeen. Løsninger i drift, Build Lab åpent."],
  ["14:30", "Hovedscenen · eksemplene", "Norske ledere som har tatt kunstig intelligens i drift."],
  ["16:00", "Avslutningsforedraget", "Dagen samles i én konklusjon."],
  ["18:30", "Summit Dinner", "350 gjester. Lukket."],
];
const Dagen = ({ nr, mottaker }) => (
  <F nr={nr} label="Dagen" mottaker={mottaker} className="hd-day kd-day hd-light">
    <h2 className="hd-h2" data-testid="kd-dag-h">Én sal. Ett program.<br />Alle ser det samme.</h2>
    <p className="kd-day-sub" data-testid="kd-dag-sub">Programmet er skissert; rekkefølge og lengder settes sammen med programkomiteen. Det som ligger fast: én sal, ingen parallelle sesjoner, og et program laget for ledere.</p>
    <ol className="hd-timeline" data-testid="kd-dag-timeline">
      {DAG.map(([t, l, d, hi]) => <li key={t} className={hi ? "hi" : ""}><span className="t">{t}</span><i className="pt" /><span className="l">{l}</span><span className="d">{d}</span></li>)}
    </ol>
    <figure className="kd-strip" data-testid="kd-dag-strip"><span className="im"><Img src={IMG.publikum} alt="Publikum i Peer Gynt-salen" draggable={false} /></span><figcaption>Peer Gynt-salen · Grieghallen</figcaption></figure>
    <p className="hd-dayfoot" data-testid="kd-dag-foot">700 ledere går gjennom samme dag og går ut med de samme referansene.</p>
  </F>
);

/* 11 Formatene — fire formater venstre, hovedscenen høyre */
const FORMAT = [
  ["Hovedscenen", "Foredrag og samtaler med ledere som har tatt kunstig intelligens i drift. Internasjonalt nivå, norsk relevans."],
  ["Ledersamtalene", "Lukkede samtaler i mindre grupper om det som ikke sies fra scenen: ansvar, sikkerhet, kostnader og folk."],
  ["Build Lab", "En fungerende løsning på et reelt forretningsproblem bygges gjennom dagen — med Replit som Build Partner (ja i prinsippet)."],
  ["AI Experience", "Foajeen: løsninger i drift, klyngene, studentene og fagmiljøene. Prøv selv — ingen stands."],
];
const Formatene = ({ nr, mottaker }) => (
  <F nr={nr} label="Formatene" mottaker={mottaker} className="kd-format hd-light">
    <h2 className="hd-h2 big" data-testid="kd-format-h">Vist, ikke lovet.</h2>
    <p className="kd-format-sub" data-testid="kd-format-sub">Fire formater bærer dagen. Felles for dem: det som vises, finnes. Løsninger i drift, ledere som har tatt beslutningene, og programvare som bygges mens salen ser på.</p>
    <ol className="kd-format-grid" data-testid="kd-format-points">{FORMAT.map(([t, d], i) => <li key={t}><span className="n">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span></li>)}</ol>
    <Ph src={IMG.scene} alt="Hovedscenen" cap="Hovedscenen · konseptvisualisering" className="kd-format-ph" testid="kd-format-ph" />
    <p className="bd-foot" data-testid="kd-format-foot">Flere formater er i arbeid. Bare det som er avtalt eller sagt ja til, står her.</p>
  </F>
);

/* 12 Mer enn scenen — konseptbilder i deckets palett: opplevelsesflaten, foajeen, middagen */
const MER = [
  ["AI Experience", "Se det. Prøv det. Møt dem som bygger det.", IMG.expo],
  ["Nettverket", "Konsernsjefer, gründere, investorer, forskere og politikere — i Grieghallens foajé, med byen utenfor.", IMG.nett],
  ["Summit Dinner", "Samtalen fortsetter etter at scenen er stengt.", IMG.dinner],
];
const MerEnnScenen = ({ nr, mottaker }) => (
  <F nr={nr} label="Mer enn scenen" mottaker={mottaker} className="hd-beyond kd-mer hd-light">
    <h2 className="hd-h2" data-testid="kd-mer-h">Konferansen er større<br />enn scenen.</h2>
    <div className="hd-frags" data-testid="kd-mer-frags">
      {MER.map(([n, d, img]) => (
        <figure className="hd-frag" key={n}>
          <div className="ph"><Img src={img} alt={n} draggable={false} /></div>
          <figcaption><span className="n">{n}</span><span className="d">{d}</span></figcaption>
        </figure>
      ))}
    </div>
    <span className="vd-concept" data-testid="kd-mer-cap">Konseptvisualiseringer</span>
  </F>
);

/* 13 Standarden — fire prinsipper, to og to */
const STD = [
  ["Språket", "Nøkternt. Ingen «revolusjon», ingen løfter om framtiden. Det som sies fra scenen, skal tåle et styremøte dagen etter — og et intervju uken etter."],
  ["Produksjonen", "Internasjonalt nivå i Peer Gynt-salen: lyd, lys, bilde og regi som en nasjonal arena krever. Én sal, én regi, ingen kompromisser på det som vises."],
  ["Kurateringen", "Programmet settes av arrangøren og programkomiteen. Partnere kjøper ikke taletid, og ingen står på scenen fordi de har betalt for det."],
  ["Partnerne", "Én aktør per kategori, med en reell rolle i programmet. «Leveres med», ikke «sponset av». Færre logoer, tydeligere roller."],
];
const Standarden = ({ nr, mottaker }) => (
  <F nr={nr} label="Standarden" mottaker={mottaker} className="kd-std hd-light">
    <h2 className="hd-h2" data-testid="kd-std-h">Slik holder vi nivået.</h2>
    <p className="kd-lede" data-testid="kd-std-sub">En nasjonal arena bygges på tillit: at programmet er uavhengig, at språket er presist og at produksjonen holder. Fire prinsipper som gjelder alt vi lager.</p>
    <ol className="kd-std-grid" data-testid="kd-std-points">{STD.map(([t, d], i) => <li key={t}><span className="n">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span></li>)}</ol>
    <p className="hd-exec-foot" data-testid="kd-std-foot">Tema 2027: The Next Shift · én fast dato i året</p>
  </F>
);

/* 14 Rekkevidde — distribusjonsplanen, uten oppfunne tall. Broadcast-bildet er en konseptvisualisering. */
const REKKEVIDDE = [
  ["Lanseringen", "Programmet, hovedtaleren og partnerne offentliggjøres samlet — ett nasjonalt nyhetsøyeblikk."],
  ["Innen 24 timer", "Klipp fra åpningsforedraget, samtalen og Build Lab — klippet for kanalene som betyr noe."],
  ["Etterpå", "Hele sesjoner tilgjengelig etter konferansen; hver talers innslag som eget klipp."],
  ["Gjennom året", "LinkedIn, YouTube og presse — og en redaksjonell serie som holder samtalen i gang til neste gang."],
];
const Rekkevidde = ({ nr, mottaker }) => (
  <F nr={nr} label="Rekkevidde" mottaker={mottaker} className="hd-reach vd-reach kd-reach hd-light">
    <h2 className="hd-h2" data-testid="kd-reach-h">Bygget for 700.<br />Laget for å nå lenger.</h2>
    <ul className="hd-reach-list" data-testid="kd-reach-list">{REKKEVIDDE.map(([k, d]) => <li key={k}><span className="k">{k}</span><span className="d">{d}</span></li>)}</ul>
    <div className="hd-reach-ph"><Img src={IMG.broadcast} alt="Produksjon ved hovedscenen — konseptvisualisering" draggable={false} /></div>
    <span className="vd-concept kd-reach-cap" data-testid="kd-reach-cap">Konseptvisualisering</span>
    <p className="hd-reach-foot" data-testid="kd-reach-foot">Distribusjonsplanen bygges sammen med programmet. Tall for rekkevidde kommer når de er reelle — vi finner dem ikke opp.</p>
  </F>
);

/* 15 Måter å bli med — uten rolle: fem roller, ingen penger. Med rolle-flagg («-programkomite» osv.): «En rolle vi tror kan passe.» — én rolle stort,
   de andre som én linje. Bergen-varianten får i tillegg én konkret, valgfri idé for byens gründere og investorer. */
const ROLLER = [
  ["Programkomité", "Kvalitetssikre programmet og forankre konferansen i fagmiljøene. Under etablering.", "Noen få møter fram mot april 2027"],
  ["Taler", "Norske ledere som har tatt kunstig intelligens i drift — i samtale på hovedscenen eller i ledersamtalene.", "Én sesjon, forberedt sammen med oss"],
  ["Ambassadør", "Åpne dører til ledere og virksomheter som bør sitte i salen.", "Introduksjoner når det passer"],
  ["Partner", "Én aktør per kategori, med en reell rolle i programmet. Ikke en logo på en vegg.", "Egen samtale, eget materiale"],
  ["Deltaker", "Invitasjonene sendes i 2027. Setene er begrenset, og sammensetningen styres.", "Én dag i Bergen, 13. april 2027"],
];
const ROLLEN = {
  programkomite: {
    t: "Programkomiteen", tail: "hvordan vi ser for oss programkomiteen",
    d: "Vi ønsker å samle en liten gruppe erfarne ledere som kan utfordre programmet, foreslå relevante perspektiver og bidra til at konferansen treffer problemstillingene norske beslutningstakere faktisk står i.",
    o: "Forpliktelsen skal være lav: noen få korte møter fram mot april 2027. Ingen operativ rolle og ingen økonomisk forpliktelse.",
    andre: ["Taler", "Ambassadør", "Partner", "Deltaker"],
  },
  taler: {
    t: "Taler", tail: "programmet — og hvor {m} kan passe inn i det",
    d: "Vi ønsker norske ledere som har tatt kunstig intelligens i drift, i samtale på hovedscenen eller i ledersamtalene — om det som faktisk virket, og det som ikke gjorde det.",
    o: "Én sesjon, forberedt sammen med oss. Ingen salgsbudskap, ingen økonomisk forpliktelse.",
    andre: ["Programkomité", "Ambassadør", "Partner", "Deltaker"],
  },
  ambassador: {
    t: "Ambassadør", tail: "hva en ambassadørrolle kan innebære",
    d: "Vi ønsker noen få ledere som kan åpne dører til virksomheter og mennesker som bør sitte i salen — og si fra når vi tar feil.",
    o: "Introduksjoner når det passer. Ingen operativ rolle og ingen økonomisk forpliktelse.",
    andre: ["Programkomité", "Taler", "Partner", "Deltaker"],
  },
  partner: {
    t: "Partner", tail: "hva et partnerskap kan være",
    d: "Én aktør per kategori, med en reell rolle i programmet — ikke en logo på en vegg. Hva rollen skal være, finner vi ut sammen.",
    o: "Egen samtale, eget materiale. Dette dokumentet ber ikke om noe.",
    andre: ["Programkomité", "Taler", "Ambassadør", "Deltaker"],
  },
};
const BergenLinje = () => (
  <p className="kd-roller-line kd-roller-bergen" data-testid="kd-roller-line"><span className="k">For Bergen</span>Gründerne og investorene i byen får sin plass i AI Experience og i én lukket samtale om kapital og kunstig intelligens. Hvem som er vertskap for den, er åpent.</p>
);
const BliMed = ({ nr, mottaker, category }) => {
  const r = ROLLEN[rolleOf(category)];
  const bergen = bergenOf(category);
  if (r) {
    return (
      <F nr={nr} label="En rolle vi tror kan passe" mottaker={mottaker} className="kd-roller kd-rolle hd-light">
        <h2 className="hd-h2" data-testid="kd-roller-h">En rolle vi tror kan passe.</h2>
        <section className="kd-rolle-main" data-testid="kd-rolle-main">
          <span className="k">Rollen</span>
          <span className="t">{r.t}</span>
          <p className="d">{r.d}</p>
          <p className="o">{r.o}</p>
        </section>
        <div className="kd-rolle-andre" data-testid="kd-rolle-andre">
          <span className="k">Andre måter å være med</span>
          <ul>{ROLLER.filter(([t]) => r.andre.includes(t)).map(([t, d]) => <li key={t}><b>{t}</b><span>{d}</span></li>)}</ul>
        </div>
        {bergen && <BergenLinje />}
        <p className="bd-foot" data-testid="kd-roller-foot">Ingen av rollene forutsetter penger. Status står ved hvert navn i alt vårt materiale, og ingenting oppgis som avtalt før det er det.</p>
      </F>
    );
  }
  return (
    <F nr={nr} label="Måter å bli med" mottaker={mottaker} className="kd-roller hd-light">
      <h2 className="hd-h2" data-testid="kd-roller-h">Måter å bli med.</h2>
      <p className="kd-lede" data-testid="kd-roller-lede">Dette dokumentet ber ikke om noe. Det forklarer hva vi bygger, slik at samtalen etterpå kan handle om hvor {mottaker || "du"} kan bidra. Fem måter å være med:</p>
      <ol className="kd-roller-grid" data-testid="kd-roller-rows">{ROLLER.map(([t, d, o], i) => <li key={t}><span className="n">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span><span className="o"><i>Omfang</i>{o}</span></li>)}</ol>
      {bergen
        ? <BergenLinje />
        : <p className="kd-roller-line" data-testid="kd-roller-line">Ingen av rollene forutsetter penger. Partnerskap er egne samtaler, med eget materiale.</p>}
      <p className="bd-foot" data-testid="kd-roller-foot">{bergen ? "Ingen av rollene forutsetter penger. " : ""}Status står ved hvert navn i alt vårt materiale, og ingenting oppgis som avtalt før det er det.</p>
    </F>
  );
};

/* 16 Neste steg — Grieghallen i blåtimen. Et spørsmål, ikke en forutsetning: «Kunne det vært interessant å høre litt mer?» */
const NesteSteg = ({ nr, mottaker, category }) => {
  const r = ROLLEN[rolleOf(category)];
  const tail = r ? r.tail.replace("{m}", mottaker || "du") : `hvor ${mottaker || "dere"} kan bidra`;
  return (
    <F nr={nr} label="Neste steg" mottaker={mottaker} className="hd-place kd-end">
      <Img className="hd-place-img" src={IMG.hus} alt="Grieghallen, Bergen" draggable={false} />
      <div className="hd-place-shade" aria-hidden />
      <div className="hd-place-txt">
        <span className="k" data-testid="kd-end-foot">Norwegian AI Summit · Grieghallen, Bergen · 13. april 2027 · The Next Shift</span>
        <h2 data-testid="kd-end-h">Kunne det vært interessant<br />å høre litt mer?</h2>
        <p className="d" data-testid="kd-end-p">Vi tar gjerne en uformell prat om hva vi bygger, hvor vi står og {tail}. En halvtime er nok.</p>
        <p className="s" data-testid="kd-contact">Martin Kviteberg · Daglig leder <small>· martin@norwegianaisummit.no · +47 480 99 558</small></p>
      </div>
    </F>
  );
};

/* Rekkefølge: konsept → momentum → team/nettverk. Uten rolle 16 slides; med rolle (programkomité o.l.) kuttes Mer enn scenen og Rekkevidde → 14. */
const SLIDES = (bergen, rolle) => [
  Cover, bergen ? ByenBergen : Arenaen, Momentum, HvemStarBak, NettverketNo, Rommet, Temaet, HvorforNorge, Programmet, Dagen, Formatene,
  ...(rolle ? [] : [MerEnnScenen]), Standarden, ...(rolle ? [] : [Rekkevidde]), BliMed, NesteSteg,
];
const slidesFor = (_m, category) => SLIDES(bergenOf(category), rolleOf(category));

const ASSETS = [...CINE_ASSETS, KEVIN, ...Object.values(IMG), ...TEAM.map((t) => t[2]), ...LEVERES_ASSETS, ...NETT_ASSETS];

export function KonseptPrint() {
  return <DeckPrint slides={slidesFor} fallback="" />;
}

export default function KonseptDeck({ mottaker = "", publicMode = false }) {
  return <DeckViewer deck="konsept" slides={slidesFor} fallback={mottaker} filePrefix="Norwegian-AI-Summit-Konseptet" assets={ASSETS} publicMode={publicMode} categoryField="variant" no />;
}
